import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { select } from '@shared/datastore/operators/select';
import { Observable } from 'rxjs/Observable';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { ErrorTracking } from '../tracking';
import { RootModelAction } from './actions';
import { ApiHttp } from './api-http.service';
import {
  arrayIsShallowEqual,
  isDefined,
  objectFilterByKeys,
  sameElements,
  setDiff,
  toNumber,
  toObservable,
} from './helpers';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { duplicateFirst } from './operators/duplicateFirst';
import { Query } from './query';
import { RootModel, RootModelSearch } from './root.model';
import { transformSearch } from './search/search.transformers';
import { stringifyQuery } from './store.helpers';
import {
  Pagination,
  Path,
  QueryParams,
  Reference,
  StoreState,
} from './store.model';
import { WebSocketService } from './websocket';

@Injectable()
export class Datastore {
  constructor(
    private store: Store<StoreState>,
    private apiHttp: ApiHttp,
    private webSocketService: WebSocketService,
    private errorTracking: ErrorTracking,
  ) {}

  list<K extends keyof RootModel>(
    key1: K,
    key2: string | Observable<string>,
    queryFn?: (q: Query<K>) => Query<K>,
  ): ListObservable<K> {
    const rawQuery = queryFn ? queryFn(new Query()).query : {};

    const refStream: Observable<Reference<K>> = Observable.combineLatest(
      [
        toObservable(key1),
        toObservable(key2),
        toObservable(rawQuery.limit),
        toObservable(rawQuery.offset),
        toObservable(rawQuery.params),
      ],
      (
        path1: K,
        path2: string,
        limit: number,
        offset: number,
        params: QueryParams<RootModel[K]>,
      ) => {
        const path: Path<K> = [path1, path2, undefined];
        return { path, query: { limit, offset, params } };
      },
    ).pipe(
      tap(ref => {
        const action: RootModelAction<K> = {
          type: 'REQUEST_DATA',
          payload: {
            type: ref.path[0],
            ref,
          },
        };
        this.store.dispatch(action);
      }),
      shareReplay(1),
    );
    return new ListObservable(
      refStream,
      this.apiHttp,
      this.store,
      this.errorTracking,
      refStream.pipe(
        switchMap(({ path: [path1, path2], query }) =>
          this.store.pipe(
            select(path1, path2),
            map(data => {
              const queryString = stringifyQuery<K>(query);
              if (data && data.lists && data.lists[queryString]) {
                return ((data.lists[queryString] || { ids: [] }).ids || [])
                  .slice((query && query.offset) || 0)
                  .map(id => data.entities[id].entity);
              }
              return undefined;
            }),
            filter(isDefined),
            distinctUntilChanged(arrayIsShallowEqual),
          ),
        ),
      ),
    );
  }

  /* Returns an observable with a single object.
   */
  object<K extends keyof RootModel>(
    key1: K,
    key2: string | Observable<string>,
    objectId: string | number | Observable<string> | Observable<number>,
  ): ObjectObservable<K> {
    const refStream = Observable.combineLatest(
      [
        toObservable(key1),
        toObservable(key2),
        toObservable<number | string>(objectId).pipe(distinctUntilChanged()),
      ],
      (path1: K, path2: string, id: string | number) => {
        const path: [K, string, string[]] = [path1, path2, [id.toString()]];
        return { path };
      },
    ).pipe(
      switchMap(
        ({ path: [path1, path2] }) =>
          this.store.pipe(select(path1, path2), take(1)),
        ({ path }, data) => ({ ref: { path }, data }),
      ),
      tap(({ ref, data }) => {
        const { path: [, , ids] } = ref;
        // Don't fetch if this was fetched in the last second.
        if (
          !data ||
          !data.entities[ids[0]] ||
          toNumber(data.entities[ids[0]].timeFetched) + 1000 < Date.now()
        ) {
          const action: RootModelAction<K> = {
            type: 'REQUEST_DATA',
            payload: {
              type: ref.path[0],
              ref,
            },
          };
          this.store.dispatch(action);
        }
      }),
      map(({ ref }) => ref),
      shareReplay(1),
    );

    const subscriptionStream =
      key1 === 'onlineOffline'
        ? this.webSocketService
            .subscribeOnlineOffline(
              refStream.pipe(
                map(({ path: [path1, path2, [id]] }) => parseInt(id, 10)),
              ),
            )
            .pipe(combineLatest(refStream, (ss, ref) => ref))
        : refStream;

    return new ObjectObservable(
      Observable.merge(refStream, subscriptionStream).pipe(
        switchMap(({ path: [path1, path2, ids] }) =>
          this.store.pipe(
            select(path1, path2),
            filter(isDefined),
            map(obj => obj.entities[ids[0]] && obj.entities[ids[0]].entity),
            filter(isDefined),
            distinctUntilChanged(),
          ),
        ),
      ),
    );
  }

  /* Returns a list of objects at a location.
   * If an observable is passed in for key2 then only the delta will be
   * refetched from the network.
   */
  objects<K extends keyof RootModel>(
    key1: K,
    key2: string | Observable<string>,
    objectIds:
      | ReadonlyArray<number>
      | ReadonlyArray<string>
      | Observable<ReadonlyArray<number>>
      | Observable<ReadonlyArray<string>>,
  ): Observable<ReadonlyArray<RootModel[K]>> {
    const refStream = Observable.combineLatest(
      [
        toObservable(key1),
        toObservable(key2),
        toObservable<ReadonlyArray<number> | ReadonlyArray<string>>(
          objectIds,
        ).pipe(distinctUntilChanged(arrayIsShallowEqual)),
      ],
      (
        path1: K,
        path2: string,
        ids: ReadonlyArray<number | string>, // This is a bug in TS
      ) => {
        const path: [K, string, string[]] = [
          path1,
          path2,
          ids.map(id => id.toString()),
        ];
        return { path };
      },
    ).pipe(
      // Add an path at the start with an empty list of ids
      duplicateFirst<{ path: [K, string, string[]] }>(
        ({ path: [path1, path2] }) => ({ path: [path1, path2, []] }),
      ),
      pairwise(), // Use pairwise and only fetch new things
      tap(([{ path: [, , idsOrig] }, { path: [type, path2, idsNew] }]) => {
        const newIds = setDiff(idsNew, idsOrig);
        if (newIds.length) {
          const action: RootModelAction<K> = {
            type: 'REQUEST_DATA',
            payload: { type, ref: { path: [type, path2, newIds] } },
          };
          this.store.dispatch(action);
        }
      }),
      map(([{ path: path1 }, { path: path2 }]) => ({ path: path2 })),
      shareReplay(1),
    );

    return refStream.pipe(
      switchMap(({ path: [path1, path2, ids] }) =>
        this.store.pipe(
          select(path1, path2),
          filter(isDefined),
          map(entitiesObject =>
            objectFilterByKeys<StoreState[K][string]['entities'][string]>(
              entitiesObject.entities,
              ids,
            ),
          ),
          map(entitiesObject => Object.values(entitiesObject)),
          map(entities => entities.map(entity => entity.entity)),
          distinctUntilChanged(sameElements),
        ),
      ),
    );
  }
  // Hits an elastic search endpoint directly and returns the result.
  // The data doesn't get stored in the datastore (at least yet).
  search<K extends keyof RootModelSearch>(
    key: K,
    query: string | Observable<string>,
    pagination: Pagination,
  ): Observable<RootModelSearch[K]> {
    return this.apiHttp
      .fetchSearch(key, toObservable(query))
      .pipe(map(result => transformSearch(key, result)));
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/datastore.ts